import { useCallback } from 'react';
import { emailService } from '../services/api';
import { getCookie, setCookie } from '../utils/cookieUtils';
import { useEmailContext } from '../contexts/EmailContext';
import { toast } from 'react-toastify';

export const useEmailInit = () => {
    const [emailState, setEmailState] = useEmailContext();

    const initializeEmail = useCallback(async () => {
        try {
            const sessionId = getCookie('sessionId');
            let emailData;
            
            if (!sessionId) {
                emailData = await emailService.generateEmail();
                setCookie('sessionId', emailData.data.sessionId);
            } else {
                try {
                    emailData = await emailService.getCurrentEmail(sessionId);
                } catch (error) {
                    if (error.response?.status === 410) {
                        setCookie('sessionId', '', -1);
                        emailData = await emailService.generateEmail();
                        setCookie('sessionId', emailData.data.sessionId);
                    } else {
                        throw error;
                    }
                }
            }
            
            setEmailState({
                tempEmail: emailData.data.emailAddress,
                createdAt: new Date(emailData.data.createdAt),
                validTill: new Date(emailData.data.validTill),
                remainingTimeInSeconds: emailData.data.remainingTimeInSeconds,
                loading: false,
                error: null,
            });

            return emailData;
        } catch (error) {
            console.error('Error initializing email:', error);
            toast.error(error.response?.data?.message || 'Failed to initialize email address.');
            setEmailState({
                tempEmail: null,
                createdAt: null,
                validTill: null,
                remainingTimeInSeconds: null,
                loading: false,
                error: error.response?.data?.message || 'Failed to initialize email address.',
            });
            return null;
        }
    }, [setEmailState]);

    const reinitializeEmail = useCallback(async () => {
        try {
            // Clear the existing session
            setCookie('sessionId', '', -1);
            
            // Generate new email
            const emailData = await emailService.generateEmail();
            
            // Set new session
            setCookie('sessionId', emailData.data.sessionId);
            
            setEmailState({
                tempEmail: emailData.data.emailAddress,
                createdAt: new Date(emailData.data.createdAt),
                validTill: new Date(emailData.data.validTill),
                remainingTimeInSeconds: emailData.data.remainingTimeInSeconds,
                loading: false,
                error: null,
            });

            toast.success('New email address generated!');
            return emailData;
        } catch (error) {
            console.error('Error reinitializing email:', error);
            toast.error(error.response?.data?.message || 'Failed to reinitialize email address.');
            setEmailState({
                tempEmail: null,
                createdAt: null,
                validTill: null,
                remainingTimeInSeconds: null,
                loading: false,
                error: error.response?.data?.message || 'Failed to reinitialize email address.',
            });
            return null;
        }
    }, [setEmailState]);

    const extendEmailTime = useCallback(async () => {
        try {
            const sessionId = getCookie('sessionId');
            if (!sessionId) {
                throw new Error('No active session');
            }

            try {
                const emailData = await emailService.extendEmailTime(sessionId);
                
                setEmailState({
                    tempEmail: emailData.data.emailAddress,
                    createdAt: new Date(emailData.data.createdAt),
                    validTill: new Date(emailData.data.validTill),
                    remainingTimeInSeconds: emailData.data.remainingTimeInSeconds,
                    loading: false,
                    error: null,
                });

                toast.success('Email time extended by 1 hour!');
                return emailData;
            } catch (error) {
                if (error.response?.status === 410) {
                    // If email has expired, generate a new one
                    return reinitializeEmail();
                }
                throw error;
            }
        } catch (error) {
            console.error('Error extending email time:', error);
            toast.error(error.response?.data?.message || 'Failed to extend email time.');
            setEmailState(prev => ({
                ...prev,
                error: error.response?.data?.message || 'Failed to extend email time.',
            }));
            return null;
        }
    }, [setEmailState, reinitializeEmail]);

    return {
        ...emailState,
        initializeEmail,
        reinitializeEmail,
        extendEmailTime,
    };
}; 